/**
 * Centralized analytics service for tracking admin actions
 *
 * This utility provides standardized methods for tracking events across the admin application
 * with consistent properties and error handling.
 */
import posthog from 'posthog-js';
import { config } from '../config';

// Common properties to include with all events
const getCommonProperties = () => {
  const route = globalThis.location.pathname;
  return {
    page_path: route,
    timestamp: new Date().toISOString(),
    app_version: config.appVersion,
    environment: config.environment,
    app_type: 'admin',
  };
};

/**
 * Track an admin action safely with standardized properties
 *
 * @param eventName - The name of the event to track
 * @param properties - Additional properties to include with the event
 * @returns boolean indicating if tracking was successful
 */
export const trackEvent = (
  eventName: string,
  properties: Record<string, any> = {},
): boolean => {
  try {
    // Check if PostHog is available and initialized
    if (!posthog.isFeatureEnabled) {
      // Check if PostHog is properly initialized
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('PostHog not loaded. Event not tracked:', eventName);
      }
      return false;
    }

    posthog.capture(eventName, {
      ...getCommonProperties(),
      ...properties,
    });
    return true;
  } catch (err) {
    // Silent fail in production, log in development
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error('Error tracking event:', err);
    }
    return false;
  }
};

/**
 * Track page view with additional metadata
 *
 * @param pageTitle - The title of the page being viewed
 * @param properties - Additional properties to include with the event
 */
export const trackPageView = (
  pageTitle: string,
  properties: Record<string, any> = {},
): boolean => {
  return trackEvent('admin_page_view', {
    page_title: pageTitle,
    ...properties,
  });
};

/**
 * Track admin content management actions
 */
export const trackContentAction = (
  actionType: string,
  contentType: string,
  contentId?: string,
  additionalData?: Record<string, any>,
): boolean => {
  return trackEvent('admin_content_action', {
    action_type: actionType, // create, edit, delete, publish, etc.
    content_type: contentType,
    content_id: contentId,
    ...additionalData,
  });
};

/**
 * Track admin user management actions
 */
export const trackUserAction = (
  actionType: string,
  userId?: string,
  userRole?: string,
  additionalData?: Record<string, any>,
): boolean => {
  return trackEvent('admin_user_action', {
    action_type: actionType, // create, edit, delete, suspend, etc.
    user_id: userId,
    user_role: userRole,
    ...additionalData,
  });
};

/**
 * Track admin organization management actions
 */
export const trackOrganizationAction = (
  actionType: string,
  organizationId?: string,
  additionalData?: Record<string, any>,
): boolean => {
  return trackEvent('admin_organization_action', {
    action_type: actionType,
    organization_id: organizationId,
    ...additionalData,
  });
};

/**
 * Track admin campaign management actions
 */
export const trackCampaignAction = (
  actionType: string,
  campaignId?: string,
  campaignType?: string,
  additionalData?: Record<string, any>,
): boolean => {
  return trackEvent('admin_campaign_action', {
    action_type: actionType,
    campaign_id: campaignId,
    campaign_type: campaignType,
    ...additionalData,
  });
};

/**
 * Track admin settings changes
 */
export const trackSettingsChange = (
  settingCategory: string,
  settingName: string,
  oldValue?: any,
  newValue?: any,
): boolean => {
  return trackEvent('admin_settings_change', {
    setting_category: settingCategory,
    setting_name: settingName,
    old_value:
      typeof oldValue === 'object' ? JSON.stringify(oldValue) : oldValue,
    new_value:
      typeof newValue === 'object' ? JSON.stringify(newValue) : newValue,
  });
};

/**
 * Track errors that occur in the admin application
 */
export const trackError = (
  errorMessage: string,
  errorStack?: string,
  componentName?: string,
  additionalInfo?: Record<string, any>,
): boolean => {
  return trackEvent('admin_error', {
    error_message: errorMessage,
    error_stack: errorStack,
    component: componentName || 'unknown',
    ...additionalInfo,
  });
};

/**
 * Setup global error tracking
 * Call this once in your app setup
 */
export const setupErrorTracking = (): void => {
  if (typeof globalThis !== 'undefined') {
    globalThis.addEventListener('error', (event) => {
      trackError(event.message, event.error?.stack, 'window', {
        line: event.lineno,
        column: event.colno,
      });
    });

    globalThis.addEventListener('unhandledrejection', (event) => {
      trackError(
        'Unhandled Promise Rejection',
        event.reason?.stack || String(event.reason),
        'promise',
      );
    });
  }
};
